/**
 * Questionnaire options are derived from real `services` tags so filtering
 * matches the slider. Steps 2–3 change with previous answers.
 * Labels use i18n via the `t` function passed into getters.
 */

import { services as defaultServices } from "./services";

const PHASE_ORDER = [
  "idea",
  "poc",
  "prototype",
  "mvp",
  "ready_product",
  "legacy_system",
];

const FOCUS_ORDER = [
  "mobile_development",
  "web_development",
  "ui_ux_design",
  "devops_services",
  "cloud_solutions",
  "leverage_ai_and_automation",
  "manage_and_analyse_your_data",
  "build_consistent_data_strategy",
  "digital_transformation",
  "improve_app_maintainability",
  "software_architecture",
  "business_analysis",
  "project_management",
  "bolster_security_and_compliance",
  "quality_assurance",
  "it_consulting",
  "legacy_system",
];

const PHASE_TAG_SET = new Set(PHASE_ORDER);

const PRIORITY_ORDER = [
  "cloud_infrastructure",
  "docker_kubernetes",
  "cicd_implementation",
  "aws_expertise",
  "bolster_security_and_compliance",
  "quality_assurance",
  "software_architecture",
  "business_analysis",
  "project_management",
  "build_consistent_data_strategy",
  "manage_and_analyse_your_data",
  "leverage_ai_and_automation",
  "optimise_and_future_proof",
  "become_more_agile",
  "manage_technical_issues",
  "it_consulting",
  "digital_transformation",
  "improve_app_maintainability",
];

export const ASSESSMENT_STEPS = [
  {
    id: "phase",
    titleKey: "assessment.steps.phase.title",
    descriptionKey: "assessment.steps.phase.description",
  },
  {
    id: "focus",
    titleKey: "assessment.steps.focus.title",
    descriptionKey: "assessment.steps.focus.description",
  },
  {
    id: "priority",
    titleKey: "assessment.steps.priority.title",
    descriptionKey: "assessment.steps.priority.description",
  },
];

function collectTags(serviceList) {
  const out = new Set();
  serviceList.forEach((s) => s.tags.forEach((t) => out.add(t)));
  return out;
}

export function formatTagForDisplay(tag, t) {
  if (tag == null) return "";
  if (typeof t !== "function") return String(tag).replace(/_/g, " ");
  const key = `assessment.tags.${tag}`;
  const label = t(key);
  if (label === key) return String(tag).replace(/_/g, " ");
  return label;
}

function toOptions(values, t) {
  return values.map((value) => ({
    value,
    label: formatTagForDisplay(value, t),
  }));
}

export function getPhaseOptions(allServices = defaultServices, t) {
  const present = collectTags(allServices);
  const values = PHASE_ORDER.filter((p) => present.has(p));
  return toOptions(values, t);
}

export function getFocusOptions(phase, allServices = defaultServices, t) {
  if (!phase) return [];
  const relevant = allServices.filter((s) => s.tags.includes(phase));
  const present = collectTags(relevant);
  const values = FOCUS_ORDER.filter((f) => f !== phase && present.has(f));
  return toOptions(values, t);
}

export function getPriorityOptions(
  phase,
  focus,
  allServices = defaultServices,
  t
) {
  if (!t || !phase || !focus) return [];

  const strict = allServices.filter(
    (s) => s.tags.includes(phase) && s.tags.includes(focus)
  );
  let pool = collectTags(strict);

  const pickOrdered = (tagSet) =>
    PRIORITY_ORDER.filter(
      (tag) =>
        tagSet.has(tag) &&
        tag !== phase &&
        tag !== focus &&
        !PHASE_TAG_SET.has(tag)
    );

  let values = pickOrdered(pool);

  if (values.length < 2) {
    const wide = allServices.filter((s) => s.tags.includes(phase));
    pool = collectTags(wide);
    values = pickOrdered(pool);
  }

  if (values.length === 0) {
    values = ["optimise_and_future_proof", "it_consulting"].filter((tag) =>
      collectTags(allServices).has(tag)
    );
  }

  return toOptions(values, t);
}
